import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import CategoryFilters from "@/pages/category/components/CategoryFilters";
import { filterTitles, paginate, SortKey, TitleFilters, allTags } from "@/utils/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { TitleType } from "@/mocks/titles";

const pageSize = 8;

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "updated", label: "更新时间" },
  { key: "rating", label: "评分最高" },
  { key: "heat", label: "热度排行" },
];

const typeCopy: Record<TitleType, { title: string; label: string; en: string; desc: string }> = {
  movie: {
    title: "电影分类",
    label: "电影",
    en: "Movies",
    desc: "从悬疑犯罪到科幻冒险，按地区、年份、类型与评分自由组合筛选，找到今晚想看的那一部。",
  },
  tv: {
    title: "电视剧分类",
    label: "电视剧",
    en: "TV Series",
    desc: "短剧集与长剧集一并收录。追剧之前，先看看评分、类型与更新情况。",
  },
};

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const currentType: TitleType = type === "tv" ? "tv" : "movie";
  const copy = typeCopy[currentType];

  const [filters, setFilters] = useState<TitleFilters>({});
  const [sort, setSort] = useState<SortKey>("updated");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useDocumentMeta(
    `${copy.title} - 午夜影院`,
    `午夜影院${copy.label}分类页，收录多地区、多类型${copy.label}作品，支持按地区、年份、类型与评分筛选。`
  );

  const activeCount = useMemo(
    () =>
      [filters.region, filters.year, filters.genre, filters.tag, filters.minRating].filter(
        (value) => value !== undefined && value !== ""
      ).length,
    [filters]
  );

  const result = useMemo(() => {
    const list = filterTitles(currentType, filters, sort);
    return paginate(list, page, pageSize);
  }, [currentType, filters, sort, page]);

  const updateFilters = (next: TitleFilters) => {
    setFilters(next);
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  useEffect(() => {
    setFilters({});
    setPage(1);
    setFilterOpen(false);
  }, [currentType]);

  return (
    <div className="w-full bg-background-50">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <span className="pointer-events-none absolute -right-4 top-1/2 hidden -translate-y-1/2 font-heading text-[120px] font-bold uppercase tracking-tight text-background-50/5 md:block">
          {copy.en}
        </span>
        <div className="relative mx-auto w-full max-w-[1280px] px-4 py-9 md:px-6 md:py-12">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-background-400">
            <Link to="/" className="transition-colors duration-300 hover:text-secondary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-200">{copy.title}</span>
          </nav>

          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl font-semibold tracking-tight text-background-50 md:text-[32px]">
                {copy.title}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-300/85">
                {copy.desc}
              </p>
            </div>
            <p className="font-label text-xs text-background-400">
              共 <span className="text-secondary-300">{result.total}</span> 部作品符合条件
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-8 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-[248px_1fr] lg:gap-10">
        <aside className="hidden lg:block">
          <div className="sticky top-[96px] max-h-[calc(100vh-120px)] overflow-y-auto rounded-lg border border-background-200 bg-background-100 p-4">
            <CategoryFilters
              filters={filters}
              onChange={updateFilters}
              onClear={clearFilters}
              activeCount={activeCount}
              tags={allTags}
            />
          </div>
        </aside>

        <div>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-700 transition-colors duration-200 hover:border-accent-400 hover:text-accent-700 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选
                {activeCount > 0 ? (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 font-label text-[10px] text-primary-950">
                    {activeCount}
                  </span>
                ) : null}
              </button>

              {activeCount > 0 ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="cursor-pointer whitespace-nowrap text-xs text-foreground-500 transition-colors duration-200 hover:text-accent-700"
                >
                  清除筛选
                </button>
              ) : (
                <p className="text-xs text-foreground-500">按更新时间为你排序</p>
              )}
            </div>

            <div className="flex items-center gap-1 rounded-md border border-background-200 bg-background-100 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setSort(option.key);
                    setPage(1);
                  }}
                  className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs transition-colors duration-200 ${
                    sort === option.key
                      ? "bg-primary-900 text-background-50"
                      : "text-foreground-600 hover:text-accent-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {result.total === 0 ? (
            <EmptyState
              icon="ri-search-eye-line"
              title="没有找到符合条件的作品"
              description="试试减少筛选条件，或者直接清除全部筛选，看看完整的片单。"
              actionLabel="清除筛选"
              onAction={clearFilters}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-5 xl:grid-cols-4">
                {result.items.map((item) => (
                  <MovieCard key={item.id} item={item} />
                ))}
              </div>
              <Pagination page={result.page} totalPages={result.totalPages} onChange={setPage} />
            </>
          )}
        </div>
      </div>

      {filterOpen ? (
        <div className="fixed inset-0 z-[60] flex flex-col bg-background-50 lg:hidden">
          <div className="flex items-center justify-between border-b border-background-200 px-4 py-3.5">
            <p className="font-heading text-sm font-semibold text-foreground-950">筛选{ copy.label }</p>
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              aria-label="关闭筛选"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <CategoryFilters
              filters={filters}
              onChange={updateFilters}
              onClear={clearFilters}
              activeCount={activeCount}
              tags={allTags}
            />
          </div>
          <div className="border-t border-background-200 px-4 py-3">
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="w-full cursor-pointer whitespace-nowrap rounded-md bg-accent-500 py-3 text-sm font-medium text-primary-950"
            >
              查看 {result.total} 部作品
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}